import { ReactNode } from 'react'

import { FlexContainer } from '../../../FlexContainer'
import { Label } from '../../../Label'
import { Tooltip } from '../../../Tooltip'
import * as S from '../../CheckPicker.styles'
import { CheckPickerItemType } from '../../CheckPicker.types'

const MULTIPLY_FACTOR_FONT_SIZE = 6
const PADDING_SIZE = 4
const TOGGLE_INSET = 12

interface TagLimits {
  maxTagWidth?: number
  tagLimit?: number
  shouldLimitTagVisibility?: boolean
}

interface VisibleTagCountParams<OptionType extends string> extends TagLimits {
  selectedOptions: CheckPickerItemType<OptionType>[]
  innerWidth: number
}

interface SelectedTagsProps<OptionType extends string> extends TagLimits {
  options?: CheckPickerItemType<OptionType>[]
  value?: OptionType[]
  title?: string
  width: number
  selectedElement: ReactNode
}

const getTagLabel = <OptionType extends string>(option: CheckPickerItemType<OptionType>) =>
  typeof option.label === 'string' ? option.label : option.customInputLabel

const countVisibleTags = <OptionType extends string>({
  selectedOptions,
  innerWidth,
  maxTagWidth,
  tagLimit,
  shouldLimitTagVisibility,
}: VisibleTagCountParams<OptionType>) => {
  const visibleLabels: (string | undefined)[] = []

  for (const option of selectedOptions) {
    const visibleCount = visibleLabels.length
    const labelsLength =
      `${option.label || ''} ${visibleLabels.join(' ')}`.length * MULTIPLY_FACTOR_FONT_SIZE +
      PADDING_SIZE
    const hasSurpassedHardLimit = maxTagWidth == null && visibleCount > (tagLimit || 1)
    const hasSurpassedWidthLimit = (visibleCount + 1) * (maxTagWidth ?? 1) >= innerWidth
    const hasSurpassedVisibilityLimit = shouldLimitTagVisibility
      ? labelsLength >= innerWidth
      : hasSurpassedHardLimit || hasSurpassedWidthLimit

    if (hasSurpassedVisibilityLimit) {
      return visibleCount
    }
    visibleLabels.push(getTagLabel(option))
  }

  return visibleLabels.length
}

export const SelectedTags = <OptionType extends string>({
  options,
  value,
  title,
  width,
  selectedElement,
  ...tagLimits
}: SelectedTagsProps<OptionType>) => {
  const titleElement = title ? <S.CustomTitle>{title}</S.CustomTitle> : null

  if (!options) {
    return (
      <>
        {titleElement} {selectedElement}
      </>
    )
  }

  const selectedOptions = options.filter(option =>
    value?.find(selectedValue => selectedValue === option.value),
  )
  const visibleCount = countVisibleTags({
    selectedOptions,
    innerWidth: width - TOGGLE_INSET,
    ...tagLimits,
  })
  const visibleOptions = selectedOptions.slice(0, visibleCount)
  const hiddenCount = selectedOptions.length - visibleCount
  const visibleLabels = visibleOptions.map(getTagLabel)

  const tags = visibleOptions.map(option => (
    <Label.Small
      key={`${option.value}-label`}
      value={getTagLabel(option)}
      style={option.labelStyle}
    />
  ))

  const hiddenOptions = options
    .filter(option => value?.includes(option.value))
    .filter(option => !visibleLabels.some(visibleLabel => visibleLabel === option.label))

  const overflowTag = (
    <Tooltip
      key={`+${hiddenCount}`}
      content={
        <FlexContainer gap={3} direction="column" noWrap>
          {hiddenOptions.map(option => (
            <Label.Small key={`${option.value}-tooltip`} value={option.label} />
          ))}
        </FlexContainer>
      }
      placement="top"
    >
      <S.CustomLabelWrapper>
        <Label.Small value={`+${hiddenCount}`} />
      </S.CustomLabelWrapper>
    </Tooltip>
  )

  return (
    <FlexContainer justify="flex-start" noWrap>
      {titleElement} {hiddenCount > 0 ? [...tags, overflowTag] : tags}
    </FlexContainer>
  )
}
